export type SlotPreviewInput = {
  dayStartTime: string;
  lessonDurationMinutes: number;
  breakDurationMinutes: number;
  /** 1 = her ders arasi; 2–4 = N ders peş peşe, sonra N × dinlenme. */
  breakEveryNLessons?: number;
  lunchStartTime: string;
  lunchDurationMinutes: number;
  dailyLessonCount: number;
};

export type PreviewSlot = { index: number; start: string; end: string };

export type PreviewTimelineItem =
  | { kind: "lesson"; index: number; start: string; end: string }
  | { kind: "break"; start: string; end: string; durationMinutes: number }
  | { kind: "free"; start: string; end: string }
  | { kind: "lunch"; start: string; end: string };

export type SlotPreviewResult = {
  slots: PreviewSlot[];
  timeline: PreviewTimelineItem[];
  computedDayEnd: string;
  requestedLessonCount: number;
  actualLessonCount: number;
  midnightCapped: boolean;
};

const MINUTES_PER_DAY = 24 * 60;

function parseHHmm(value: string): number {
  const v = value.length >= 5 ? value.slice(0, 5) : value;
  const m = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(v);
  if (!m) return 0;
  return Number(m[1]) * 60 + Number(m[2]);
}

function toHHmm(total: number): string {
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function normalizeBreakEveryN(value: number | undefined): number {
  const n = Math.floor(Number(value ?? 1));
  if (!Number.isFinite(n) || n < 1) return 1;
  if (n > 4) return 4;
  return n;
}

function breakAfterLesson(
  lessonIndex: number,
  breakDurationMinutes: number,
  breakEveryNLessons: number,
  isLastLesson: boolean,
): number {
  if (isLastLesson) return 0;
  const unit = Math.max(0, breakDurationMinutes);
  if (lessonIndex % breakEveryNLessons === 0) return breakEveryNLessons * unit;
  return 0;
}

function pushFreeGap(timeline: PreviewTimelineItem[], startMin: number, endMin: number) {
  if (endMin > startMin) {
    timeline.push({ kind: "free", start: toHHmm(startMin), end: toHHmm(endMin) });
  }
}

function pushLunch(
  timeline: PreviewTimelineItem[],
  lunchStart: number,
  lunchEnd: number,
  lunchShown: { value: boolean },
) {
  if (!lunchShown.value) {
    timeline.push({
      kind: "lunch",
      start: toHHmm(lunchStart),
      end: toHHmm(lunchEnd),
    });
    lunchShown.value = true;
  }
}

/**
 * Dinlenme satiri ekler. Ogle penceresine tasan kisim gosterilmez
 * (ogle ayri satirda kalir).
 */
function pushBreakGap(
  timeline: PreviewTimelineItem[],
  breakStart: number,
  gapMinutes: number,
  lunchStart: number,
  lunchEnd: number,
  lunchShown: boolean,
) {
  if (gapMinutes <= 0) return;
  let breakEnd = breakStart + gapMinutes;
  if (!lunchShown && breakStart < lunchEnd && breakEnd > lunchStart) {
    if (breakStart >= lunchStart) return;
    breakEnd = lunchStart;
  }
  if (breakEnd <= breakStart) return;
  timeline.push({
    kind: "break",
    start: toHHmm(breakStart),
    end: toHHmm(breakEnd),
    durationMinutes: breakEnd - breakStart,
  });
}

export function previewScheduleSlots(input: SlotPreviewInput): SlotPreviewResult {
  const requested = Math.max(1, Math.floor(input.dailyLessonCount) || 1);
  const start = parseHHmm(input.dayStartTime);
  const lunchStart = parseHHmm(input.lunchStartTime);
  const lunchEnd = lunchStart + input.lunchDurationMinutes;
  const lesson = input.lessonDurationMinutes;
  const breakMin = input.breakDurationMinutes;
  const everyN = normalizeBreakEveryN(input.breakEveryNLessons);

  const slots: PreviewSlot[] = [];
  const timeline: PreviewTimelineItem[] = [];
  const lunchShown = { value: false };
  let cursor = start;
  let midnightCapped = false;

  for (let idx = 1; idx <= requested; idx += 1) {
    const candidateEnd = cursor + lesson;
    if (candidateEnd > MINUTES_PER_DAY) {
      midnightCapped = true;
      break;
    }

    const isLast = idx === requested;

    if (cursor < lunchEnd && candidateEnd > lunchStart) {
      pushFreeGap(timeline, cursor, lunchStart);
      pushLunch(timeline, lunchStart, lunchEnd, lunchShown);
      cursor = lunchEnd;
      const afterLunchEnd = cursor + lesson;
      if (afterLunchEnd > MINUTES_PER_DAY) {
        midnightCapped = true;
        break;
      }
      const slot = { index: idx, start: toHHmm(cursor), end: toHHmm(afterLunchEnd) };
      slots.push(slot);
      timeline.push({ kind: "lesson", ...slot });
      const gap = breakAfterLesson(idx, breakMin, everyN, isLast);
      pushBreakGap(timeline, afterLunchEnd, gap, lunchStart, lunchEnd, lunchShown.value);
      cursor = afterLunchEnd + gap;
      continue;
    }

    const slot = { index: idx, start: toHHmm(cursor), end: toHHmm(candidateEnd) };
    slots.push(slot);
    timeline.push({ kind: "lesson", ...slot });
    const gap = breakAfterLesson(idx, breakMin, everyN, isLast);
    pushBreakGap(timeline, candidateEnd, gap, lunchStart, lunchEnd, lunchShown.value);
    cursor = candidateEnd + gap;
  }

  const computedDayEnd = slots.length > 0 ? slots[slots.length - 1].end : toHHmm(start);

  if (!lunchShown.value) {
    if (slots.length > 0) {
      const lastEnd = parseHHmm(slots[slots.length - 1].end);
      // Son dersten sonra mola yok; ogleden once kalan bosluk "Bos Zaman".
      if (lastEnd < lunchStart) {
        pushFreeGap(timeline, lastEnd, lunchStart);
      }
    }
    pushLunch(timeline, lunchStart, lunchEnd, lunchShown);
  }

  timeline.sort((a, b) => {
    const byStart = parseHHmm(a.start) - parseHHmm(b.start);
    if (byStart !== 0) return byStart;
    const order = { lesson: 0, break: 1, free: 2, lunch: 3 } as const;
    return order[a.kind] - order[b.kind];
  });

  return {
    slots,
    timeline,
    computedDayEnd,
    requestedLessonCount: requested,
    actualLessonCount: slots.length,
    midnightCapped,
  };
}
